import { cryptoProxyHost, useCryptoProxy, getToken, SERVER_HOST } from '../SharedCommon/utils';
import cryptoUtils from './cryptoproxy/cryptoUtil';

export function getWebSocketUrl() {
  const token = getToken();
  if (!token) {
    return null;
  }

  if (useCryptoProxy) {
    const base = cryptoProxyHost.replace(/^http/, 'ws');
    return `${base.replace(/\/+$/, '')}/wss?token=${encodeURIComponent(token)}`;
  }

  let host = SERVER_HOST || '';
  if (host.startsWith('http://') || host.startsWith('https://')) {
    host = host.replace(/^https?:\/\//, '');
  }

  const isLocalHost = /^localhost(:\d+)?$/.test(host) || /^127\.0\.0\.1(:\d+)?$/.test(host);
  const protocol =
    isLocalHost && typeof window !== 'undefined' && window.location && window.location.protocol === 'http:'
      ? 'ws:'
      : 'wss:';

  return `${protocol}//${host.replace(/\/+$/, '')}/wss?token=${encodeURIComponent(token)}`;
}

export default class GTSocket {
  socket = null;
  id = '';
  useNewFav = false;
  // eslint-disable-next-line
  newMessageCallback = (data) => {};
  // eslint-disable-next-line
  deleteMessageCallback = (data) => {};
  connectedCallback = () => {};
  disconnectedCallback = () => {};
  reconnectedCallback = () => {};
  groupTagsCallback = () => {};
  newGroupInfoCallback = () => {};

  isExplicitClose = false;
  reconnectTimer = null;
  reconnectDelay = 2000;
  hasConnectedOnce = false;

  setCallback(receiveNewMessage) {
    this.newMessageCallback = receiveNewMessage;
  }

  constructor({ id, onNewMessage, getTags = true, shareInGroups = '', useNewFav = false }) {
    this.init({ id, onNewMessage, getTags, shareInGroups, useNewFav });
  }

  init({ id, onNewMessage, getTags = true, shareInGroups = '', useNewFav = false }) {
    console.log('init socket for: ', id, getTags, shareInGroups, useNewFav);

    this.id = id;
    this.newMessageCallback = onNewMessage;
    this.useNewFav = useNewFav;

    this.closeSocket();
    this.isExplicitClose = false;

    this.connect();
  }

  connect() {
    const url = getWebSocketUrl();
    if (!url) {
      console.log('no init socket: missing token or URL');
      return;
    }

    console.log('websocket connecting to:', url);

    const WebSocketImpl =
      typeof WebSocket !== 'undefined' ? WebSocket : typeof window !== 'undefined' ? window.WebSocket : null;
    if (!WebSocketImpl) {
      console.error('WebSocket is not supported in this environment');
      return;
    }

    let ws;
    try {
      ws = new WebSocketImpl(url);
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      this.scheduleReconnect();
      return;
    }

    if (window.___socket && window.___socket !== this && typeof window.___socket.close === 'function') {
      try {
        window.___socket.close();
      } catch (e) {
        // ignore
      }
    }
    window.___socket = this;
    this.socket = ws;

    ws.onopen = () => {
      if (this.socket !== ws) {
        return;
      }
      console.log('socket connected!');
      if (this.hasConnectedOnce) {
        this.reconnectedCallback();
      }
      this.hasConnectedOnce = true;
      this.reconnectDelay = 2000;
      this.connectedCallback();
    };

    ws.onmessage = (event) => {
      if (this.socket !== ws) {
        return;
      }
      try {
        let rawData = event.data;
        if (
          useCryptoProxy &&
          typeof rawData === 'string' &&
          !rawData.trim().startsWith('{') &&
          !rawData.trim().startsWith('[')
        ) {
          try {
            rawData = cryptoUtils.decryptSession(rawData);
          } catch (e) {
            console.warn('decryptSession failed:', e);
          }
        }
        const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        if (!parsed) {
          return;
        }

        const { event: evt, data, groupId } = parsed;

        // If groupId is present in the message and this socket is scoped to a group, filter by group
        if (groupId && this.id && String(groupId) !== String(this.id)) {
          return;
        }

        switch (evt) {
          case 'newMessages':
            this.onNewMessage(data);
            break;
          case 'deleteMessage':
            this.deleteMessageCallback(data);
            break;
          case 'groupTags':
            this.groupTagsCallback(data);
            break;
          case 'newGroupInfo':
            this.newGroupInfoCallback(data);
            break;
          default:
            console.log('Unhandled websocket event:', evt, data);
        }
      } catch (err) {
        console.error('Error handling WebSocket message:', err);
      }
    };

    ws.onerror = (error) => {
      if (this.socket !== ws) {
        return;
      }
      console.log('WebSocket error:', error);
    };

    ws.onclose = (event) => {
      if (this.socket !== ws) {
        return;
      }
      console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`);
      this.disconnectedCallback();
      if (!this.isExplicitClose && event.code !== 4001 && getToken()) {
        this.scheduleReconnect();
      }
    };
  }

  scheduleReconnect() {
    if (this.isExplicitClose || this.reconnectTimer) {
      return;
    }

    const delay = this.reconnectDelay + Math.floor(Math.random() * 1000);
    console.log(`Scheduling WebSocket reconnect in ${delay}ms`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isExplicitClose && getToken()) {
        this.connect();
      }
    }, delay);

    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000);
  }

  onNewMessage(data) {
    try {
      if (!Array.isArray(data)) {
        console.log('Invalid data received from server!', data);
        return;
      }
      data.sort((msg1, msg2) => {
        return msg2.time - msg1.time;
      });
      this.newMessageCallback(data);
    } catch (error) {
      console.log('onNewMessage: ', error, data);
    }
  }

  async sendMessageAsync(data) {
    console.log(data);
  }

  closeSocket() {
    this.isExplicitClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      try {
        s.close();
      } catch (e) {
        // ignore
      }
    }
    if (window.___socket === this) {
      window.___socket = null;
    }
  }

  close() {
    this.closeSocket();
  }
}
